import {
  Alert,
  Box,
  Divider,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Invocations } from "@/Actions/components";
import { useAuth } from "@/providers";
import {
  PageBreadcrumbs,
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTabPanel,
} from "@/shared/components";
import { TriggerEvaluationsForTrigger } from "@/shared/components/TriggerEvaluations";
import { useTrigger } from "@/shared/domain/actions/hooks";

import { TriggerDetails } from "../components/trigger";

export function Trigger() {
  const { triggerName } = useParams<{ triggerName: string | undefined }>();

  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const triggerQuery = useTrigger(triggerName, {
    resourceOwnerId: currentOrganization?.org_id,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const tabSearchParam = searchParams.get("tab");
  const tabValue = tabSearchParam !== null ? parseInt(tabSearchParam) : 0;

  const triggerView = triggerQuery.data;

  const Breadcrumbs = (
    <PageBreadcrumbs>
      <Link to="/triggers">Triggers</Link>
      <span>{triggerName}</span>
    </PageBreadcrumbs>
  );

  if (triggerQuery.isLoading || (!triggerView && !triggerQuery.error)) {
    return (
      <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
        <CircularProgress />
      </PageLayout>
    );
  }

  let errorDisplay = null;
  if (triggerQuery.isError) {
    errorDisplay = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        <Alert severity="error">{triggerQuery.error.message}</Alert>
      </Box>
    );
  } else if (!triggerView) {
    errorDisplay = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        <Alert severity="error">
          Failed to load trigger &apos;{triggerName}&apos;.
          <br />
          Try refreshing the page.
        </Alert>
      </Box>
    );
  }

  let pageContent = null;
  if (currentOrganization && triggerView) {
    pageContent = (
      <>
        <RobotoTabPanel index={0} value={tabValue}>
          <TriggerDetails org={currentOrganization} trigger={triggerView} />
        </RobotoTabPanel>

        <RobotoTabPanel index={1} value={tabValue}>
          <Alert
            severity="info"
            sx={{
              marginBottom: theme.spacing(1),
              marginTop: theme.spacing(1),
            }}
          >
            Trigger evaluations are records of when a trigger was run for a
            given dataset. Trigger evaluations are initiated by platform events,
            such as when a file is uploaded to a dataset or when a
            dataset&apos;s metadata is updated.
          </Alert>
          <TriggerEvaluationsForTrigger
            triggerId={triggerView.trigger_id}
            triggerName={triggerName}
            triggerType={triggerView.trigger_type}
          />
        </RobotoTabPanel>

        <RobotoTabPanel
          index={2}
          value={tabValue}
          sx={{
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
          }}
        >
          <Invocations
            triggerId={triggerView.trigger_id}
            currentOrg={currentOrganization}
          />
        </RobotoTabPanel>
      </>
    );
  } else if (!currentOrganization) {
    pageContent = (
      <Typography color={theme.palette.error.main} variant="h4">
        No organization selected
      </Typography>
    );
  }

  return (
    <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
      {errorDisplay}

      {!triggerQuery.error && (
        <>
          <Box
            sx={{
              paddingTop: theme.spacing(0.5),
            }}
          >
            <RobotoStyledTabs
              value={tabValue}
              onChange={(_event, newValue) => {
                setSearchParams({ tab: String(newValue) });
              }}
              aria-label="triggers tabs"
            >
              <RobotoStyledTab label="Details" />
              <RobotoStyledTab label="Evaluation History" />
              <RobotoStyledTab label="Invocations" />
            </RobotoStyledTabs>

            <Divider
              sx={{
                borderColor: theme.border.color,
              }}
            />
          </Box>
          <Box
            sx={{
              padding: theme.spacing(1, 0),
              paddingBottom: theme.spacing(1),
            }}
          >
            {pageContent}
          </Box>
        </>
      )}
    </PageLayout>
  );
}
